import { IMoreMenuItem } from '@sales-center/components'
import { IImport } from '@user/store/import/typings'
import { useCallback, useMemo } from 'react'
import { useHandlers } from './useHandlers'
import { useDownloadErrorsHandler } from './useDownloadErrorsHandler'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { differenceInDays } from 'date-fns'

export function useMenu(props: IImport) {
  const { status, createdDate } = props

  const { downloadFile } = useHandlers(props)
  const { onDownloadErrorsClick } = useDownloadErrorsHandler(props)

  const tidalThemeActive = useTidalThemeActive()

  const capitalizeFirstLetter = useCallback((string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  }, [])

  const capitalizeEachWord = useCallback(
    (string: string) => {
      return string
        .split(' ')
        .map((word) => capitalizeFirstLetter(word))
        .join(' ')
    },
    [capitalizeFirstLetter],
  )

  const formatLabel = useCallback(
    (label: string) => {
      return tidalThemeActive
        ? capitalizeFirstLetter(label)
        : capitalizeEachWord(label)
    },
    [tidalThemeActive, capitalizeFirstLetter, capitalizeEachWord],
  )

  const actionMenuOptions: IMoreMenuItem[] = useMemo(() => {
    const fileNotAvailable =
      differenceInDays(new Date(), new Date(createdDate)) > 180

    return [
      {
        label: formatLabel('Download Completed Records'),
        onClick: () => console.log('Download completed records'),
        disabled: true,
        hidden: true,
      },
      {
        label: formatLabel('Download Original File'),
        onClick: downloadFile,
        disabled: fileNotAvailable,
        tooltipText: fileNotAvailable
          ? 'Original files are only available for 180 days from the date of the original import.'
          : undefined,
      },
      {
        label: formatLabel('Download Error File'),
        onClick: onDownloadErrorsClick,
        hidden: status !== 'CompletedWithErrors',
        disabled: fileNotAvailable,
        tooltipText: fileNotAvailable
          ? 'Error files are only available for 180 days from the date of the original import.'
          : 'This file will contain records with errors that were skipped during the review and edit step in the import process',
      },
      {
        label: formatLabel('Delete Records'),
        onClick: () => console.log('Delete records'),
        hidden: true,
      },
    ]
  }, [formatLabel, status, downloadFile, onDownloadErrorsClick, createdDate])

  return {
    actionMenuOptions,
  }
}

export type UseMenu = ReturnType<typeof useMenu>
